import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { apiFetch } from '../utils/api';
import { 
  Search, 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  X, 
  Truck, 
  FileText, 
  Save, 
  Filter,
  DollarSign,
  Tag
} from 'lucide-react';

const AdminOrders = () => {
  const { user } = useContext(AuthContext);
  
  // State for orders list, pagination, and filters
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(false);
  
  // Filter states
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('All');
  const [paymentMethod, setPaymentMethod] = useState('All');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('createdAt:desc');

  // Drawer states
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editStatus, setEditStatus] = useState('');
  const [editPaymentStatus, setEditPaymentStatus] = useState('');
  const [editTrackingNumber, setEditTrackingNumber] = useState('');
  const [editAdminNotes, setEditAdminNotes] = useState('');

  // Fetch orders list based on filters
  const fetchOrders = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page,
        limit,
        search,
        status,
        paymentMethod,
        startDate,
        endDate,
        sortBy
      });

      const res = await apiFetch(`/api/admin/orders?${queryParams.toString()}`);
      const data = await res.json();
      if (res.ok) {
        setOrders(data.orders || []);
        setPages(data.pages || 1);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, page, status, paymentMethod, startDate, endDate, sortBy]);

  // Trigger search on Enter key
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      setPage(1);
      fetchOrders();
    }
  };

  // Open drawer and load order details
  const handleOpenDrawer = async (orderId) => {
    try {
      const res = await apiFetch(`/api/admin/orders/${orderId}`);
      const data = await res.json();
      if (res.ok) {
        setSelectedOrder(data);
        setEditStatus(data.status);
        setEditPaymentStatus(data.paymentStatus);
        setEditTrackingNumber(data.trackingNumber || '');
        setEditAdminNotes(data.adminNotes || '');
        setDrawerOpen(true);
      }
    } catch (err) {
      console.error('Failed to load order details:', err);
    }
  };

  // Save changes to order status, payment status, tracking number, and notes
  const handleSaveChanges = async () => {
    if (!selectedOrder) return;
    try {
      setIsUpdating(true);
      const res = await apiFetch(`/api/admin/orders/${selectedOrder._id}`, {
        method: 'PUT',
        body: JSON.stringify({
          status: editStatus,
          paymentStatus: editPaymentStatus,
          trackingNumber: editTrackingNumber,
          adminNotes: editAdminNotes
        })
      });
      const data = await res.json();
      if (res.ok) {
        // Update local list item
        setOrders(orders.map(o => o._id === selectedOrder._id ? data : o));
        setSelectedOrder(data);
        alert('Order updated successfully!');
      } else {
        alert(data.message || 'Failed to update order');
      }
    } catch (err) {
      console.error('Failed to save changes:', err);
      alert('Error updating order');
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusBadge = (statusValue) => {
    switch (statusValue) {
      case 'Pending':
        return <span className="badge badge-pending">Pending</span>;
      case 'Verified':
        return <span className="badge badge-verified">Verified</span>;
      case 'Processing':
        return <span className="badge badge-processing">Processing</span>;
      case 'Shipped':
        return <span className="badge badge-shipped">Shipped</span>;
      case 'Delivered':
        return <span className="badge badge-delivered">Delivered</span>;
      case 'Cancelled':
        return <span className="badge badge-cancelled">Cancelled</span>;
      default:
        return <span className="badge badge-default">{statusValue}</span>;
    }
  };

  const getPaymentBadge = (payStatus) => {
    if (payStatus === 'Paid') {
      return <span style={{ color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem', border: '1px solid rgba(16,185,129,0.2)' }}>Paid</span>;
    }
    if (payStatus === 'Partial') {
      return <span style={{ color: '#3b82f6', background: 'rgba(59,130,246,0.1)', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem', border: '1px solid rgba(59,130,246,0.2)' }}>Partial</span>;
    }
    if (payStatus === 'Failed') {
      return <span style={{ color: '#ef4444', background: 'rgba(239,68,68,0.1)', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem', border: '1px solid rgba(239,68,68,0.2)' }}>Failed</span>;
    }
    if (payStatus === 'Refunded') {
      return <span style={{ color: '#ec4899', background: 'rgba(236,72,153,0.1)', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem', border: '1px solid rgba(236,72,153,0.2)' }}>Refunded</span>;
    }
    return <span style={{ color: '#f59e0b', background: 'rgba(245,158,11,0.1)', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem', border: '1px solid rgba(245,158,11,0.2)' }}>Pending</span>;
  };

  const formatPaymentMethod = (method) => {
    if (method === 'full_prepaid') return 'Full Prepaid';
    if (method === 'advance') return '20% Advance';
    return method;
  };

  return (
    <div style={containerStyle}>
      <style>{cssRules}</style>

      {/* Header */}
      <div style={headerStyle}>
        <div>
          <h2 style={{ color: 'var(--color-text-primary)', margin: 0, fontSize: '1.8rem', fontWeight: '700' }}>Manage Orders</h2>
          <p style={{ color: 'var(--color-text-secondary)', margin: '5px 0 0 0', fontSize: '0.95rem' }}>View and update customer wholesale orders</p>
        </div>
        <div style={{ color: '#ff7a00', background: 'rgba(255,122,0,0.05)', border: '1px solid rgba(255,122,0,0.15)', padding: '8px 16px', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '600' }}>
          Total Orders: {total}
        </div>
      </div>

      {/* Toolbar / Filters */}
      <div className="filter-bar">
        <div className="filter-group" style={{ flex: '2', minWidth: '220px' }}>
          <label className="filter-label">Search Order / Customer</label>
          <div className="input-wrapper">
            <Search size={16} className="input-icon" />
            <input
              type="text"
              placeholder="Search Number, Name, Shop..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              className="filter-input"
            />
          </div>
        </div>

        <div className="filter-group">
          <label className="filter-label">Status</label>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
            className="filter-select"
          >
            <option value="All">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Verified">Verified</option>
            <option value="Processing">Processing</option>
            <option value="Shipped">Shipped</option>
            <option value="Delivered">Delivered</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">Payment Method</label>
          <select
            value={paymentMethod}
            onChange={(e) => { setPaymentMethod(e.target.value); setPage(1); }}
            className="filter-select"
          >
            <option value="All">All Methods</option>
            <option value="advance">20% Advance</option>
            <option value="full_prepaid">Full Prepaid</option>
          </select>
        </div>

        <div className="filter-group" style={{ minWidth: '150px' }}>
          <label className="filter-label">Start Date</label>
          <div className="input-wrapper">
            <Calendar size={14} className="input-icon" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="filter-input date-input"
            />
          </div>
        </div>

        <div className="filter-group" style={{ minWidth: '150px' }}>
          <label className="filter-label">End Date</label>
          <div className="input-wrapper">
            <Calendar size={14} className="input-icon" />
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="filter-input date-input"
            />
          </div>
        </div>

        <div className="filter-group">
          <label className="filter-label">Sort By</label>
          <select
            value={sortBy}
            onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
            className="filter-select"
          >
            <option value="createdAt:desc">Newest First</option>
            <option value="createdAt:asc">Oldest First</option>
            <option value="totalAmount:desc">Value: High to Low</option>
            <option value="totalAmount:asc">Value: Low to High</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '4px' }}>
          <button 
            className="btn btn-primary" 
            onClick={() => { setPage(1); fetchOrders(); }}
            style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px', height: '40px' }}
          >
            <Filter size={16} /> Apply
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div style={{ overflowX: 'auto', background: 'var(--color-surface-muted, #09090b)', borderRadius: '10px', border: '1px solid var(--color-border-light, rgba(255,255,255,0.03))' }}>
        {loading ? (
          <div style={{ padding: '80px 0', textAlign: 'center', color: '#ff7a00' }}>
            <div className="spinner"></div>
            <p style={{ marginTop: '15px' }}>Retrieving wholesale orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div style={{ padding: '80px 0', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
            <FileText size={48} style={{ opacity: 0.3, marginBottom: '15px' }} />
            <p style={{ margin: 0, fontSize: '1.1rem' }}>No matching orders found.</p>
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem', marginTop: '5px' }}>Try adjusting your search criteria or date filters.</p>
          </div>
        ) : (
          <table className="orders-table">
            <thead>
              <tr>
                <th>Order Number</th>
                <th>Customer</th>
                <th>Shop Name</th>
                <th>Phone</th>
                <th>Order Value</th>
                <th>Payment Method</th>
                <th>Status</th>
                <th>Created Date</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr key={order._id}>
                  <td style={{ fontWeight: '600', color: 'var(--color-text-primary, #f4f4f5)' }}>{order.orderNumber}</td>
                  <td>{order.user?.name || 'Guest User'}</td>
                  <td>{order.address?.shopName || order.user?.shopName || '—'}</td>
                  <td>{order.address?.phone || order.user?.phone || '—'}</td>
                  <td style={{ fontWeight: '600', color: '#ff7a00' }}>₹{order.totalAmount.toLocaleString('en-IN')}</td>
                  <td>{formatPaymentMethod(order.paymentMethod)}</td>
                  <td>{getStatusBadge(order.status)}</td>
                  <td>{new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button 
                      onClick={() => handleOpenDrawer(order._id)}
                      className="btn btn-secondary btn-sm"
                    >
                      View Order
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination Footer */}
      {pages > 1 && (
        <div className="pagination-bar">
          <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
            Showing Page <strong>{page}</strong> of <strong>{pages}</strong>
          </span>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              disabled={page === 1}
              onClick={() => setPage(prev => Math.max(prev - 1, 1))}
              className="btn btn-secondary btn-sm"
              style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              <ChevronLeft size={16} /> Prev
            </button>
            <button
              disabled={page === pages}
              onClick={() => setPage(prev => Math.min(prev + 1, pages))}
              className="btn btn-secondary btn-sm"
              style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Details Slide-out Drawer */}
      {drawerOpen && selectedOrder && (
        <div className="order-drawer-backdrop" onClick={() => setDrawerOpen(false)}>
          <div className="order-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '1.4rem', fontWeight: '700', color: 'var(--color-text-primary)' }}>{selectedOrder.orderNumber}</h3>
                <span style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>Placed on {new Date(selectedOrder.createdAt).toLocaleString()}</span>
              </div>
              <button className="close-btn" onClick={() => setDrawerOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="drawer-content">
              {/* Customer Info Section */}
              <div className="drawer-section">
                <h4 className="section-title">Customer Information</h4>
                <div className="info-grid">
                  <span className="info-label">Name</span>
                  <span className="info-val">{selectedOrder.user?.name || 'Guest User'}</span>
                  
                  <span className="info-label">Shop Name</span>
                  <span className="info-val">{selectedOrder.address?.shopName || selectedOrder.user?.shopName || '—'}</span>
                  
                  <span className="info-label">Phone</span>
                  <span className="info-val">{selectedOrder.address?.phone || selectedOrder.user?.phone || '—'}</span>
                  
                  <span className="info-label">Email</span>
                  <span className="info-val">{selectedOrder.user?.email || '—'}</span>
                </div>
              </div>

              {/* Shipping Address block */}
              <div className="drawer-section">
                <h4 className="section-title">Delivery Address</h4>
                <div style={{ background: 'var(--color-surface-2, #09090b)', padding: '15px', borderRadius: '8px', border: '1px solid var(--color-border-light, rgba(255,255,255,0.03))', color: 'var(--color-text-primary, #e4e4e7)', fontSize: '0.95rem', lineHeight: '1.6' }}>
                  <strong>{selectedOrder.address?.contactPerson || selectedOrder.user?.name}</strong><br />
                  {selectedOrder.address?.shopName && <>{selectedOrder.address.shopName}<br /></>}
                  {selectedOrder.address?.street}<br />
                  {selectedOrder.address?.city}, {selectedOrder.address?.state} - {selectedOrder.address?.postalCode}<br />
                  {selectedOrder.address?.country || 'India'}<br />
                  <span style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>Phone: {selectedOrder.address?.phone}</span>
                </div>
              </div>

              {/* Items List */}
              <div className="drawer-section">
                <h4 className="section-title">Order Items ({selectedOrder.items?.length || 0})</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="item-row">
                      <img 
                        src={item.primaryImage || '/RegardTradersLogo.png'} 
                        alt={item.productName} 
                        className="item-thumbnail" 
                        onError={(e) => { e.target.src = '/RegardTradersLogo.png'; }}
                      />
                      <div style={{ flex: 1 }}>
                        <span className="item-name">{item.productName}</span>
                        <span className="item-meta">₹{item.price.toLocaleString('en-IN')} × {item.qty} pcs</span>
                      </div>
                      <div className="item-total">
                        ₹{(item.price * item.qty).toLocaleString('en-IN')}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Applied Coupon Info Badge (Right Aligned after Order Items) */}
                {selectedOrder.coupon && selectedOrder.coupon.code && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: 'rgba(16, 185, 129, 0.08)',
                      border: '1px solid rgba(16, 185, 129, 0.25)',
                      borderRadius: '20px',
                      padding: '6px 14px',
                      fontSize: '0.85rem',
                      color: '#10b981',
                      fontWeight: '600'
                    }}>
                      <Tag size={14} />
                      <span>Applied Coupon: <strong>{selectedOrder.coupon.code}</strong> ({selectedOrder.coupon.discountType === 'FixedAmount' ? `₹${selectedOrder.coupon.discountValue} Off` : `${selectedOrder.coupon.discountValue || selectedOrder.coupon.discountPercentage}% Off`})</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Details */}
              <div className="drawer-section">
                <h4 className="section-title">Payment & Totals</h4>
                <div className="info-grid" style={{ marginBottom: '15px' }}>
                  <span className="info-label">Payment Method</span>
                  <span className="info-val" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <DollarSign size={14} style={{ color: '#ff7a00' }} />
                    {formatPaymentMethod(selectedOrder.paymentMethod)}
                  </span>
                  
                  <span className="info-label">Payment Status</span>
                  <span className="info-val">{getPaymentBadge(selectedOrder.paymentStatus)}</span>
                </div>

                <div className="summary-card">
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <span>₹{selectedOrder.subtotal?.toLocaleString('en-IN') || 0}</span>
                  </div>
                  <div className="summary-row">
                    <span>Packing Charges</span>
                    <span>₹{selectedOrder.packingCharge?.toLocaleString('en-IN') || 0}</span>
                  </div>
                  <div className="summary-row">
                    <span>Shipping Charges</span>
                    <span>₹{selectedOrder.shippingCharge?.toLocaleString('en-IN') || 0}</span>
                  </div>
                  {selectedOrder.coupon && selectedOrder.coupon.code && (
                    <div className="summary-row" style={{ color: '#10b981' }}>
                      <span>Coupon Amount</span>
                      <span style={{ fontWeight: '700' }}>-₹{selectedOrder.coupon.discountAmount?.toLocaleString('en-IN') || 0}</span>
                    </div>
                  )}
                  {selectedOrder.paymentMethod === 'full_prepaid' && (
                    <div className="summary-row" style={{ color: '#10b981' }}>
                      <span>Prepaid Discount</span>
                      <span>-₹200.00</span>
                    </div>
                  )}
                  <div className="summary-row total-row">
                    <span>Grand Total</span>
                    <span>₹{selectedOrder.totalAmount?.toLocaleString('en-IN') || 0}</span>
                  </div>
                </div>
              </div>

              {/* Admin Actions Form */}
              <div className="drawer-section" style={{ background: 'var(--color-surface-2, #1c1917)', padding: '20px', borderRadius: '10px', border: '1px solid var(--color-border-light, rgba(255,122,0,0.15))' }}>
                <h4 className="section-title" style={{ color: '#ff7a00', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Truck size={18} /> Admin Controls
                </h4>

                <div className="form-group">
                  <label className="form-label">Order Status</label>
                  <select
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                    className="form-input"
                    style={{ background: 'var(--color-surface-2, #09090b)', color: 'var(--color-text-primary)' }}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Verified">Verified</option>
                    <option value="Processing">Processing</option>
                    <option value="Shipped">Shipped</option>
                    <option value="Delivered">Delivered</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Payment Status</label>
                  <select
                    value={editPaymentStatus}
                    onChange={(e) => setEditPaymentStatus(e.target.value)}
                    className="form-input"
                    style={{ background: 'var(--color-surface-2, #09090b)', color: 'var(--color-text-primary)' }}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Partial">Partial</option>
                    <option value="Paid">Paid</option>
                    <option value="Failed">Failed</option>
                    <option value="Refunded">Refunded</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Tracking Number</label>
                  <input
                    type="text"
                    placeholder="Enter Tracking ID/URL..."
                    value={editTrackingNumber}
                    onChange={(e) => setEditTrackingNumber(e.target.value)}
                    className="form-input"
                    style={{ background: 'var(--color-surface-2, #09090b)', color: 'var(--color-text-primary)' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Admin Notes</label>
                  <textarea
                    rows="3"
                    placeholder="Internal staff notes (customer cannot see this)..."
                    value={editAdminNotes}
                    onChange={(e) => setEditAdminNotes(e.target.value)}
                    className="form-input"
                    style={{ background: 'var(--color-surface-2, #09090b)', color: 'var(--color-text-primary)', resize: 'vertical' }}
                  ></textarea>
                </div>

                <button
                  disabled={isUpdating}
                  onClick={handleSaveChanges}
                  className="btn btn-primary"
                  style={{ width: '100%', marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                >
                  <Save size={16} /> {isUpdating ? 'Saving Changes...' : 'Save Updates'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const containerStyle = { 
  maxWidth: '1200px', 
  margin: '40px auto', 
  padding: '30px', 
  background: 'var(--color-surface-1)', 
  borderRadius: '12px', 
  border: '1px solid var(--color-border-light, rgba(255,255,255,0.05))', 
  color: 'var(--color-text-primary)' 
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '30px',
  borderBottom: '1px solid var(--color-border-light, rgba(255,255,255,0.05))',
  paddingBottom: '20px',
  flexWrap: 'wrap',
  gap: '15px'
};

const cssRules = `
.filter-bar {
  display: flex;
  gap: 15px;
  margin-bottom: 25px;
  flex-wrap: wrap;
  background: var(--color-surface-muted, #09090b);
  padding: 20px;
  border-radius: 10px;
  border: 1px solid var(--color-border-light, rgba(255,255,255,0.03));
}
.filter-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 130px;
}
.filter-label {
  font-size: 0.75rem;
  color: var(--color-text-secondary, #a1a1aa);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
}
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}
.input-icon {
  position: absolute;
  left: 12px;
  color: var(--color-text-muted, #71717a);
}
.filter-input {
  width: 100%;
  background: var(--color-surface-1, #18181b);
  border: 1px solid var(--color-border-light, #27272a);
  border-radius: 6px;
  color: var(--color-text-primary, #fafafa);
  padding: 8px 12px 8px 36px;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s ease;
}
.filter-input:focus {
  border-color: #ff7a00;
}
.date-input {
  padding-left: 36px;
}
.filter-select {
  width: 100%;
  background: var(--color-surface-1, #18181b);
  border: 1px solid var(--color-border-light, #27272a);
  border-radius: 6px;
  color: var(--color-text-primary, #fafafa);
  padding: 8px 12px;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s ease;
  height: 38px;
}
.filter-select:focus {
  border-color: #ff7a00;
}
.btn {
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 10px 16px;
}
.btn-primary {
  background: #ff7a00;
  color: #000;
}
.btn-primary:hover {
  background: #e06c00;
}
.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn-secondary {
  background: var(--btn-secondary-bg, #27272a);
  color: var(--color-text-primary, #fafafa);
  border: 1px solid var(--color-border-light, rgba(255,255,255,0.05));
}
.btn-secondary:hover {
  background: var(--btn-secondary-bg-hover, #3f3f46);
}
.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn-sm {
  padding: 6px 12px;
  font-size: 0.8rem;
}
.orders-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.92rem;
  text-align: left;
}
.orders-table th {
  padding: 16px;
  border-bottom: 1px solid var(--color-border-light, rgba(255,255,255,0.05));
  color: var(--color-text-secondary, #a1a1aa);
  font-weight: 600;
}
.orders-table td {
  padding: 16px;
  border-bottom: 1px solid var(--color-border-light, rgba(255,255,255,0.02));
  color: var(--color-text-secondary, #d4d4d8);
  vertical-align: middle;
}
.orders-table tr:hover td {
  background: var(--hover-overlay, rgba(255,255,255,0.01));
}
.order-num {
  font-weight: 700;
  color: #ff7a00;
}
.badge-status {
  padding: 6px 12px;
  border-radius: 9999px;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  display: inline-block;
}
.badge-status.pending {
  background: rgba(249, 115, 22, 0.12);
  color: #f97316;
  border: 1px solid rgba(249, 115, 22, 0.2);
}
.badge-status.verified {
  background: rgba(59, 130, 246, 0.12);
  color: #3b82f6;
  border: 1px solid rgba(59, 130, 246, 0.2);
}
.badge-status.processing {
  background: rgba(168, 85, 247, 0.12);
  color: #a855f7;
  border: 1px solid rgba(168, 85, 247, 0.2);
}
.badge-status.shipped {
  background: rgba(99, 102, 241, 0.12);
  color: #6366f1;
  border: 1px solid rgba(99, 102, 241, 0.2);
}
.badge-status.delivered {
  background: rgba(16, 185, 129, 0.12);
  color: #10b981;
  border: 1px solid rgba(16, 185, 129, 0.2);
}
.badge-status.cancelled {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
}
.order-drawer-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 1100;
  display: flex;
  justify-content: flex-end;
}
.order-drawer {
  width: 100%;
  max-width: 500px;
  background: var(--color-surface-1, #0f0f12);
  height: 100%;
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease-out;
}
@keyframes slideIn {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}
.drawer-header {
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border-light, rgba(255, 255, 255, 0.05));
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.drawer-header h3 {
  margin: 0;
  font-size: 1.25rem;
  color: var(--color-text-primary);
  font-weight: 700;
}
.close-btn {
  background: transparent;
  border: none;
  color: var(--color-text-secondary, #a1a1aa);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.2s;
}
.close-btn:hover {
  background: rgba(255, 255, 255, 0.05);
}
.drawer-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}
.drawer-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.section-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #ff7a00;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid var(--color-border-light, rgba(255, 255, 255, 0.05));
  padding-bottom: 6px;
}
.info-grid {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 8px 12px;
  font-size: 0.9rem;
}
.info-label {
  color: var(--color-text-secondary, #a1a1aa);
  font-weight: 500;
}
.info-val {
  color: var(--color-text-primary);
  font-weight: 600;
}
.items-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: var(--color-surface-2, rgba(255, 255, 255, 0.01));
  border-radius: 6px;
  border: 1px solid var(--color-border-light, rgba(255,255,255,0.03));
  gap: 12px;
}
.item-thumbnail {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid rgba(255,255,255,0.08);
}
.item-name {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--color-text-primary, #fafafa);
  display: block;
}
.item-meta {
  font-size: 0.8rem;
  color: var(--color-text-secondary, #a1a1aa);
  margin-top: 2px;
  display: block;
}
.item-total {
  font-size: 0.95rem;
  font-weight: 600;
  color: #ff7a00;
}
.summary-card {
  background: var(--color-surface-2, #09090b);
  padding: 15px;
  border-radius: 8px;
  border: 1px solid var(--color-border-light, rgba(255,255,255,0.03));
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  color: var(--color-text-secondary, #a1a1aa);
}
.total-row {
  border-top: 1px solid var(--color-border-light, rgba(255,255,255,0.05));
  padding-top: 8px;
  margin-top: 4px;
  font-weight: 700;
  color: #ff7a00;
  font-size: 1.05rem;
}
.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}
.form-label {
  font-size: 0.8rem;
  color: var(--color-text-secondary, #d4d4d8);
  font-weight: 500;
}
.form-input {
  width: 100%;
  background: var(--color-surface-2, #18181b);
  border: 1px solid var(--color-border-light, #3f3f46);
  border-radius: 6px;
  color: var(--color-text-primary, #fafafa);
  padding: 10px 12px;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s ease;
}
.form-input:focus {
  border-color: #ff7a00;
}
.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255,122,0,0.1);
  border-top-color: #ff7a00;
  border-radius: 50%;
  animation: spin 0.8s infinite linear;
  margin: 0 auto;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
`;
export default AdminOrders;
